import { tz } from '@date-fns/tz';
import { api } from '@packages/backend/convex/_generated/api';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from 'convex-helpers/react';
import { endOfDay, startOfDay } from 'date-fns';
import { useEffect, useState } from 'react';
import { ErrorAlert } from '@/components/ErrorAlert';
import { OrdersTable } from '@/components/OrdersTable';
import { SuperAdminOnly } from '@/components/SuperAdminOnly';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ActionButton } from '@/components/ui+/ActionButton';
import { CalendarPopover } from '@/components/ui+/CalendarPopover';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { OrderGenerator } from './components/OrderGenerator';

const LIMIT = 20;

export const Route = createFileRoute('/r/$restaurantId/orders/')({
  component: Component,
});

function Component() {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [limitResultSet, setLimitResultSet] = useState<number | undefined>(LIMIT);

  const { restaurantId } = Route.useParams();
  const restaurantQuery = useQuery(api.restaurants.getById, { restaurantId });
  const timeZone = restaurantQuery.data?.timeZone;

  const ordersQuery = useQuery(
    api.orders.listByRestaurantId,
    timeZone
      ? {
          restaurantId,
          createdAfter: startOfDay(selectedDate, { in: tz(timeZone) }).getTime(),
          createdBefore: endOfDay(selectedDate, { in: tz(timeZone) }).getTime(),
          limit: limitResultSet,
        }
      : 'skip',
  );
  const menuQuery = useQuery(api.menus.getByRestaurantId, { restaurantId });

  useEffect(() => {
    setLimitResultSet(LIMIT);
  }, [selectedDate]);

  if (menuQuery.isError) return null; // Handled in parent layout

  if (ordersQuery.isError) {
    return <ErrorAlert message={ordersQuery.error.message} title="Error loading orders" />;
  }

  return (
    <div className="p-6 min-h-[calc(100vh-(--spacing(16)))] flex-1 flex-col gap-4 bg-muted/60 dark:bg-muted/20">
      <div className="mb-2 flex justify-between">
        <SuperAdminOnly>
          {menuQuery.data && <OrderGenerator menu={menuQuery.data} restaurantId={restaurantId} />}
        </SuperAdminOnly>
        <CalendarPopover
          date={selectedDate}
          setDate={(date) => setSelectedDate(date ?? new Date())}
        />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Orders</CardTitle>
        </CardHeader>
        <CardContent>
          {ordersQuery.isPending || menuQuery.isPending ? (
            <LoadingIcon />
          ) : (
            <OrdersTable orders={ordersQuery.data} showRestaurantColumn={false} />
          )}
          {ordersQuery.data && limitResultSet === ordersQuery.data.length && (
            <div className={'flex justify-center mt-3'}>
              <ActionButton variant={'link'} onClick={() => setLimitResultSet(undefined)}>
                Show more...
              </ActionButton>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
